import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

import { fetchPageContent, getDomain } from '../../utils/functions';
import HeroImage from '../HeroImage';
import Banner from '../Banner';
import { DrupalPage } from '../../types/drupal';
import Spinner from '../Spinner';
import Button from '../Button';

interface Props {
  title?: string;
  showIconMenu?: boolean;
  image?: {
    src: string;
    alt: string;
  };
  pageId: string;
  topContent?: JSX.Element;
  mainContent?: JSX.Element;
  secondaryContent?: JSX.Element;
  tertiaryContent?: JSX.Element;
  heroButton?: JSX.Element | null;
}

const Page: React.FC<Props> = (props: Props): JSX.Element => {
  const [pageContent, setPageContent] = useState<DrupalPage | null>();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>();
  const { t, i18n } = useTranslation('common');
  const pageTitle: { [key: string]: string } = {
    ruka: 'Ruka.fi',
    pyha: 'Pyha.fi',
  };

  useEffect(() => {
    async function getPageData() {
      setIsLoading(true);
      const data = await fetchPageContent(props.pageId, i18n.language);

      if (data) {
        setPageContent(data);
      } else {
        setErrorMessage(t('Error fetching page content.'));
      }
      setIsLoading(false);
    }

    getPageData();
  }, [i18n.language]);

  return (
    <div className='page bg-gray-200'>
      {isLoading && (
        <div className='py-20'>
          <Spinner />
        </div>
      )}

      {pageContent && pageContent.title && (
        <Helmet>
          <title>
            {pageContent.title} | {pageTitle[getDomain()]}
          </title>
        </Helmet>
      )}

      {errorMessage && <p>{errorMessage}</p>}

      {!isLoading && pageContent && (
        <>
          {pageContent.field_banner_text && (
            <Banner
              text={pageContent.field_banner_text}
              pageId={props.pageId}
              url={
                pageContent.field_banner_link
                  ? pageContent.field_banner_link.url
                  : null
              }
            />
          )}

          {pageContent.image && (
            <HeroImage
              image={pageContent.image}
              heroTitle={pageContent.field_header_title}
              heroDesc={pageContent.field_header_text}
              link={pageContent.field_header_cta}
              heroButton={props.heroButton}
            />
          )}

          {props.topContent && (
            <div className='md:relative md:-top-20 md:-mb-20 lg:px-52 xl:px-80'>
              {props.topContent}
            </div>
          )}

          <div className='px-5 py-8 md:my-0 md:px-40 lg:px-52 xl:px-80'>
            <h2 className='text-center text-2xl md:text-2xl mb-8'>
              {props.title ??
                pageContent.field_content_title ??
                pageContent.title}
            </h2>

            {pageContent.field_content_text && (
              <div
                className='my-8 md:my-12'
                dangerouslySetInnerHTML={{
                  __html: pageContent.field_content_text,
                }}
              ></div>
            )}
            {props.mainContent && props.mainContent}

            {pageContent.field_content_cta && (
              <div className='my-4'>
                <Button
                  className='md:w-fit'
                  href={pageContent.field_content_cta.url}
                  external={true}
                >
                  {pageContent.field_content_cta.title}
                </Button>
                {pageContent.field_content_cta_description && (
                  <p className='mt-4'>
                    {pageContent.field_content_cta_description}
                  </p>
                )}
              </div>
            )}

            {pageContent.field_secondary_content_title && (
              <h2 className='text-center text-2xl my-8'>
                {pageContent.field_secondary_content_title}
              </h2>
            )}

            {pageContent.field_secondary_content_text && (
              <div
                className='my-8 md:my-12'
                dangerouslySetInnerHTML={{
                  __html: pageContent.field_secondary_content_text,
                }}
              ></div>
            )}

            {pageContent.field_secondary_content_cta && (
              <div className='my-8'>
                <Button
                  className='md:w-fit'
                  href={pageContent.field_secondary_content_cta.url}
                  external={true}
                >
                  {pageContent.field_secondary_content_cta.title}
                </Button>
                {pageContent.field_secondary_cta_description && (
                  <p className='mt-4'>
                    {pageContent.field_content_cta_description}
                  </p>
                )}
              </div>
            )}

            <div>{props.secondaryContent && props.secondaryContent}</div>

            {pageContent.field_tertiary_content_title && (
              <h2 className='text-center text-2xl my-8'>
                {pageContent.field_tertiary_content_title}
              </h2>
            )}

            {pageContent.field_tertiary_content_text && (
              <div
                className='my-8 md:my-12'
                dangerouslySetInnerHTML={{
                  __html: pageContent.field_tertiary_content_text,
                }}
              ></div>
            )}

            {pageContent.field_tertiary_content_cta && (
              <div className='my-8'>
                <Button
                  className='md:w-fit'
                  href={pageContent.field_tertiary_content_cta.url}
                  external={true}
                >
                  {pageContent.field_tertiary_content_cta.title}
                </Button>
                {pageContent.field_tertiary_cta_description && (
                  <p className='mt-4'>
                    {pageContent.field_content_cta_description}
                  </p>
                )}
              </div>
            )}

            <div>{props.tertiaryContent && props.tertiaryContent}</div>
          </div>

          {pageContent.field_script && (
            <Helmet>
              <script
                async
                type='text/javascript'
                src={pageContent.field_script}
              ></script>
            </Helmet>
          )}
        </>
      )}
    </div>
  );
};

export default Page;
